import { cloneDeep } from 'lodash-es';
import { createRequiredSportFilter, resetRequiredSportFilter } from '@/helpers/sportFilter';
import { CustomTeamItem } from '@/models/customTeam';
import { createCustomTeamPageHeaders } from '@/pages/system/customTeam/CustomTeamPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { customTeamService } from '@/services/customTeamService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { usePersistentFilters } from '@/stores/composables/usePersistentFilters';
import { ICustomTeamPageFilters, ICustomTeamUpdateResponse, IListParams, IPageOptions } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'id', order: 'desc' as const }];

function createCustomTeamStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<CustomTeamItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<ICustomTeamPageFilters>({
        sportId: createRequiredSportFilter(),
        customTeamName: {
            value: null,
            title: 'customTeam.filters.customTeamName',
            allowSaveState: true,
        },
        customTeamCountryId: {
            value: null,
            title: 'customTeam.filters.countryId',
            allowSaveState: true,
        },
        customTeamTypeId: {
            value: null,
            title: 'customTeam.filters.customTeamTypeId',
            allowSaveState: true,
        },
        customTeamNameIsWomen: {
            value: null,
            title: 'customTeam.filters.customTeamIsWomen',
            allowSaveState: true,
        },
        customTeamNameIsYouth: {
            value: null,
            title: 'customTeam.filters.customTeamIsYouth',
            allowSaveState: true,
        },
    });

    // --- filter panel ---
    const hiddenFilters = ref<string[]>([]);
    const visibleFiltersMobile = ref<string[]>([]);
    const filterOrder = ref<string[]>([]);
    const useButtonForFiltersApply = ref(false);
    const saveFiltersState = ref(false);

    const availableFilters = computed(() => {
        const entries = Object.entries(filters).map(([key, meta]) => ({
            key,
            title: meta.title || '',
        }));

        if (filterOrder.value.length > 0) {
            entries.sort((a, b) => {
                const indexA = filterOrder.value.indexOf(a.key);
                const indexB = filterOrder.value.indexOf(b.key);
                return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
            });
        }

        return entries;
    });

    const visibleFilters = computed(() => Object.keys(filters).filter((key) => !hiddenFilters.value.includes(key)));

    // --- options ---
    const options = ref<IPageOptions>({
        itemsPerPage: 100,
        page: 1,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetRequiredSportFilter(filters.sportId);
        filters.customTeamName.value = null;
        filters.customTeamCountryId.value = null;
        filters.customTeamTypeId.value = null;
        filters.customTeamNameIsWomen.value = null;
        filters.customTeamNameIsYouth.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createCustomTeamPageHeaders());

    const availableColumns = computed(() => headers.value.filter((header) => header.title));

    const hiddenColumns = ref<string[]>([]);

    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title) && !header.hidden)
    );

    // --- persistence ---
    const persistentFilters = usePersistentFilters({
        filters,
        enabled: computed(() => saveFiltersState.value && storeId === 'customTeam'),
    });

    const reloadFiltersFromStorage = () => {
        resetFilters();
        if (storeId === 'customTeam' && saveFiltersState.value) {
            persistentFilters.loadFromStorage();
        }
    };

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<Record<string, any>> = {
                filters: cloneDeep(filters),
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['Country', 'AuditTable'],
            };

            const response = await customTeamService.list(params);
            items.value = (response.data.rows || []).map((row) => new CustomTeamItem(row));
            total.value = response.data.total ?? 0;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    function mergeMutationResponse(response: { data: ICustomTeamUpdateResponse }) {
        if (response.data.success === false) return;
        const row = response.data.row;
        if (!row) return;

        const idx = items.value.findIndex((i) => i.id === row.id);
        if (idx !== -1) {
            items.value[idx] = new CustomTeamItem(row);
        } else {
            items.value = [new CustomTeamItem(row), ...items.value];
            total.value += 1;
        }
        triggerRef(items);
    }

    async function patchBoolField(
        item: CustomTeamItem,
        modelKey: 'women' | 'youth' | 'isUseTradeball' | 'isImageChangedManually',
        value: 0 | 1,
        apiField: string
    ) {
        const oldValue = item[modelKey];
        item[modelKey] = value;
        triggerRef(items);

        try {
            const response = await customTeamService.update({ rows: [{ id: item.id, [apiField]: value }] });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                item[modelKey] = oldValue;
                triggerRef(items);
            }
        } catch {
            item[modelKey] = oldValue;
            triggerRef(items);
        }
    }

    function applyLogoUpload(itemId: number) {
        const item = items.value.find((i) => i.id === itemId);
        if (!item) return;
        item.imagePath = `${itemId}.png`;
        item.isImageChangedManually = 1;
        item.updatedAt = new Date().toISOString();
        triggerRef(items);
    }

    async function patchImagePath(item: CustomTeamItem, imagePath: string | null) {
        const oldPath = item.imagePath;
        const oldManual = item.isImageChangedManually;
        const oldUpdated = item.updatedAt;

        item.imagePath = imagePath;
        item.isImageChangedManually = 1;
        item.updatedAt = new Date().toISOString();
        triggerRef(items);

        try {
            const response = await customTeamService.update({
                rows: [{ id: item.id, image_path: imagePath, is_image_changed_manually: 1 }],
            });
            mergeMutationResponse(response);
            if (!response.data.row && response.data.success === false) {
                item.imagePath = oldPath;
                item.isImageChangedManually = oldManual;
                item.updatedAt = oldUpdated;
                triggerRef(items);
            }
        } catch {
            item.imagePath = oldPath;
            item.isImageChangedManually = oldManual;
            item.updatedAt = oldUpdated;
            triggerRef(items);
        }
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        patchBoolField,
        patchImagePath,
        applyLogoUpload,
        mergeMutationResponse,
        hasAdd: true,
        hasRefresh: false,
    };
}

export function useCustomTeamStore(storeId: string = 'customTeam') {
    const useStore = defineStore(storeId, () => createCustomTeamStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'customTeam'),
    });
    return useStore();
}

export type CustomTeamStoreSetup = ReturnType<typeof useCustomTeamStore>;
